import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { Textfit } from "react-textfit";
import confetti from "canvas-confetti";
import { motion, AnimatePresence } from "framer-motion";
import { Howl } from "howler";

// Sound effects
const correctSound = new Howl({ src: [`${process.env.PUBLIC_URL}/correct-sound.mp3`] });
const wrongSound = new Howl({ src: [`${process.env.PUBLIC_URL}/wrong-sound.mp3`] });
const winSound = new Howl({ src: [`${process.env.PUBLIC_URL}/win-sound.mp3`] });
const loseSound = new Howl({ src: [`${process.env.PUBLIC_URL}/lose-sound.mp3`] });



const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

const ModalPortal = ({ children }) => {
  const container = document.fullscreenElement || document.body;
  return ReactDOM.createPortal(children, container);
};

const GameOverModal = ({ onNextWord, onExit, currentWord, hasMoreWords }) => {
  return (
    <ModalPortal>
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full text-center"
          >
            <h2 className="text-3xl font-bold mb-4 text-red-600">😢 Game Over!</h2>
            <p className="text-xl mb-4 break-words text-balance">{`The word was "${currentWord}"`}</p>


            {hasMoreWords && (
              <button
                onClick={onNextWord}
                className="!mt-2 !px-6 !py-3 !bg-brand-primary !text-white !text-lg !rounded-lg block w-full"
              >
                ➡️ Next Word
              </button>
            )}

            <button
              onClick={onExit}
              className="!mt-4 !px-6 !py-3 !bg-gray-500 !text-white !text-lg !rounded-lg block w-full"
            >
              ❌ Exit Game
            </button>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    </ModalPortal>
  );
};



const Modal = ({ onNextWord, onExit, currentWord, hasMoreWords }) => {
  return (
    <ModalPortal>
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full text-center"
          >
            <h2 className="text-3xl font-bold mb-4">🎉 You Guessed It!</h2>
            <p className="text-xl mb-4 break-words text-balance">{`The word was "${currentWord}"`}</p>


            {hasMoreWords && (
              <button
                onClick={onNextWord}
                className="!mt-2 !px-6 !py-3 !bg-brand-primary !text-white !text-lg !rounded-lg block w-full"
              >
                ➡️ Next Word
              </button>
            )}

            <button
              onClick={onExit}
              className="!mt-4 !px-6 !py-3 !bg-gray-500 !text-white !text-lg !rounded-lg block w-full"
            >
              ❌ Exit Game
            </button>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    </ModalPortal>
  );
};

const removeEmojis = (str) => str.replace(/[\p{Emoji}]/gu, "");

const App = () => {
  const [customWordsInput, setCustomWordsInput] = useState("");
  const [customWordsBackup, setCustomWordsBackup] = useState("");
  const [words, setWords] = useState([]);
  const [gameStarted, setGameStarted] = useState(false);
  const [lives, setLives] = useState(10);
  const [remainingLives, setRemainingLives] = useState(5);
  const [guessedLetters, setGuessedLetters] = useState([]);
  const [currentWord, setCurrentWord] = useState(null);
  const [gameWon, setGameWon] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const fullscreenRef = useRef(null);
  const confettiCanvasRef = useRef(null);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  const [showGameOverModal, setShowGameOverModal] = useState(false);
  const [lastWord, setLastWord] = useState("");
  const [customWordsError, setCustomWordsError] = useState("");
  const loseTimeoutRef = useRef(null);
  const winTimeoutRef = useRef(null);
  const gameExitedRef = useRef(false);
  const [gameTitle, setGameTitle] = useState("");


  const [isLandscape, setIsLandscape] = useState(
    window.innerWidth > window.innerHeight && window.innerWidth < 1024
  );


  const startGame = () => {
    gameExitedRef.current = false; // ✅ Reset exit flag
  
    const randomWord = words[Math.floor(Math.random() * words.length)].toUpperCase();
    setCurrentWord(randomWord);
    setGuessedLetters([]);
    setRemainingLives(lives);
    setGameWon(false);
    setShowModal(false);
    setGameStarted(true);
  };
  

  const handleLetterClick = (letter) => {
    if (guessedLetters.includes(letter) || gameWon || remainingLives <= 0) return;
  
    setGuessedLetters((prev) => [...prev, letter]);
  
    if (!currentWord.includes(letter)) {
      setRemainingLives((prev) => prev - 1);
      wrongSound.play(); // 🔊 wrong sound
    } else {
      correctSound.play(); // 🔊 correct sound
    }
  };
  

  useEffect(() => {
    if (!currentWord) return;
  
    const allGuessed = currentWord
      .split("")
      .every((char) => {
        const isLetter = /^[A-Z]$/.test(char);
        return !isLetter || guessedLetters.includes(char);
      });
  
    if (allGuessed) {
      setGameWon(true);
      winSound.play();
  
      const canvas = confettiCanvasRef.current;
      if (canvas) {
        const myConfetti = confetti.create(canvas, { resize: true });
        myConfetti({
          particleCount: 120,
          spread: 70,
          origin: { y: 0.6 },
        });
      }
  
      winTimeoutRef.current = setTimeout(() => {
        if (gameExitedRef.current) return; // ✅ Prevent win modal if exited
        setShowModal(true);
      }, 2000);
      
    }
  
    // ✅ Cleanup function goes here
    return () => {
      clearTimeout(winTimeoutRef.current);
    };
  }, [guessedLetters, currentWord]);
  

  const handleNextWord = () => {
    const indexToRemove = words.findIndex(w => w.toUpperCase() === currentWord);
    const remaining = [...words];
  
    if (indexToRemove !== -1) {
      remaining.splice(indexToRemove, 1); // ✅ remove only one instance
    }
  
    if (remaining.length > 0) {
      setWords(remaining);
      setShowModal(false);
      setShowGameOverModal(false);
      setGameWon(false);
      setGuessedLetters([]);
      setRemainingLives(lives);
      setCurrentWord(
        remaining[Math.floor(Math.random() * remaining.length)].toUpperCase()
      );
    }
  };
  
  

  const handleExitGame = () => {
    gameExitedRef.current = true; // ✅ Mark that game was exited
    clearTimeout(loseTimeoutRef.current); // ✅ Cancel lose timeout
    clearTimeout(winTimeoutRef.current);  // ✅ Cancel win timeout

    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
    setGameStarted(false);
    setWords([]);
    setGuessedLetters([]);
    setCurrentWord(null);
    setShowModal(false);
    setShowGameOverModal(false);
    setGameWon(false);
  };
  
  
  
  const toggleFullscreen = () => {
    const element = fullscreenRef.current;
    if (!document.fullscreenElement) {
      element.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const handleConfirmWords = () => {
    setCustomWordsError(""); // ✅ Clear any previous error
    const wordArray = customWordsInput
    .split("\n")
    .map((w) => removeEmojis(w.trim())) // ✅ Remove emojis here
    .filter(Boolean);
  
  
    // Check for max words
    if (wordArray.length > 50) {
      setCustomWordsError(`You entered ${wordArray.length} words. Maximum allowed is 50.`);
      return;
    }
    
    for (let word of wordArray) {
      if (word.length > 100) {
        setCustomWordsError(`"${word}" is ${word.length} characters. Max allowed is 100.`);
        return;
    }
    
    }
  
    setWords(wordArray);
  };
  

  const displayWord = currentWord
    ? currentWord
        .split("")
        .map((char) => {
          if (char === " ") return "\u00A0";
          if (!/[A-Z]/.test(char)) return char;
          return guessedLetters.includes(char) ? char : "_";
        })
        .join(" ")
    : "";

  const getDynamicMaxFontSize = () => {
    if (!isFullscreen) return 150;
    const screenHeight = window.innerHeight;
    return Math.min(Math.floor(screenHeight * 0.3), 300);
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  useEffect(() => {
    correctSound.load();
    wrongSound.load();
    winSound.load();
    loseSound.load();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
      setIsLandscape(window.innerWidth > window.innerHeight && window.innerWidth < 1024);
    };
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
    handleResize();
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);

  useEffect(() => {
    if (remainingLives === 0 && !gameWon) {
      loseTimeoutRef.current = setTimeout(() => {
        if (gameExitedRef.current) return; // ✅ Prevent modal if game exited
  
        loseSound.play();
        if (currentWord) {
          setLastWord(currentWord);
        }
        setShowGameOverModal(true);
      }, 1500);
    }
  
    return () => {
      clearTimeout(loseTimeoutRef.current);
    };
  }, [remainingLives, gameWon, currentWord]);
  
  
  useEffect(() => {
    const type = window.HANGMAN_TYPE || null;
    const selectedId = window.HANGMAN_ID || "1";
  
    const jsonUrl = window.HANGMAN_JSON_URL || "/wp-content/plugins/hangman-themed/hangman-games.json";
    fetch(jsonUrl)    
      .then((res) => res.json())
      .then((data) => {
        if (type === "random") {
          // Flatten all words into one array
          const allWords = Object.values(data)
            .flatMap(entry => entry.words)
            .filter(Boolean);
          window.HANGMAN_WORDS = allWords;
          setGameTitle("🎲 Random Words");
        } else if (data[selectedId]) {
          window.HANGMAN_WORDS = data[selectedId].words;
          setGameTitle(data[selectedId].title || "Hangman Game");
        } else {
          console.warn("No game found for selected ID");
        }
      })
      .catch((err) => console.error("Failed to load hangman games:", err));
  }, []);
  
  
  
  
  
  return (
    <div className="flex justify-center px-4 py-6">
      <div className="w-full max-w-[1024px]">
      <div
  ref={fullscreenRef}
  className={`relative w-full ${
    !isMobileView && !gameStarted ? "aspect-[16/9]" : "h-full"
  } bg-white rounded-none shadow-brand-shadow p-4 md:p-8 flex flex-col`}
>

          {/* Canvas for fullscreen confetti */}
          <canvas
            ref={confettiCanvasRef}
            className="absolute top-0 left-0 w-full h-full pointer-events-none z-50"
          />
  
          {!gameStarted ? (
          // ✅ REPLACE WITH THIS:
words.length === 0 ? (
  <div className="flex-1 flex items-center justify-center">
    <div className="w-full max-w-lg mx-auto px-4 space-y-4">
    {gameTitle && (
  <h2 className="text-2xl font-bold text-center text-gray-800">
    {gameTitle}
  </h2>
)}

      <button
        onClick={() => setWords(window.HANGMAN_WORDS || [])}
        className="!w-full !bg-brand-secondary !text-white !py-3 !text-xl !rounded-lg"
      >
        ▶️ Play Hangman
      </button>
    </div>
  </div>
) : (
  // ✅ KEEP THIS PART — your existing lives selection UI
  <div className="flex-1 flex items-center justify-center">
    <div className="max-w-md w-full mx-auto space-y-6">
      <h2 className="text-xl md:text-3xl font-semibold text-gray-800 text-center">
        Select Number of Lives
      </h2>
      <div className="flex space-x-4 justify-center">
        {[10, 5, 3].map((num) => (
          <button
            key={num}
            onClick={() => setLives(num)}
            className={`!px-4 !py-2 !border !rounded ${
              lives === num
                ? "!bg-brand-primary !text-white !hover:bg-brand-primary"
                : "!bg-white !text-gray-800 !hover:bg-white"
            }`}
            
          >
            {num} lives
          </button>
        ))}
      </div>
      <div className="text-center">
        <button
          onClick={startGame}
          className="!mt-4 !px-6 !py-3 !bg-brand-secondary !text-white !text-xl !rounded-lg !w-full"
        >
          ▶️ Start Game
        </button>
      </div>
      <div className="text-center mt-4">
        <button
          onClick={() => setWords([])}
          className="!bg-transparent !border-none !shadow-none !p-0 !text-lg !text-blue-600 hover:!underline"
        >
          ← Back
        </button>
      </div>
    </div>
  </div>
)

          ) : (
            // Game Screen
            <div
              className={`${
                isFullscreen ? "flex-1" : "w-full md:aspect-[16/9]"
              } flex flex-col justify-between ${
                isLandscape ? "max-h-screen overflow-y-auto" : ""
              }`}
              style={
                isLandscape
                  ? {
                      height: "100vh",
                      maxHeight: "100vh",
                      padding: "8px",
                      boxSizing: "border-box",
                    }
                  : {}
              }
            >
              <div
                className={`flex justify-between items-center text-xl md:text-2xl font-semibold text-gray-800 ${
                  isLandscape ? "mb-1" : "mb-2"
                }`}
              >
                <button
                  onClick={handleExitGame}
                  className={`!bg-brand-secondary !text-white !rounded-lg text-base md:text-lg ${
                    isLandscape ? "!px-3 !py-1 text-sm" : "!px-6 !py-3"
                  }`}
                >
                  ← Exit Game
                </button>
                <button
                  onClick={toggleFullscreen}
                  className={`!bg-brand-primary !text-white !rounded-lg text-base md:text-lg ${
                    isLandscape ? "!px-3 !py-1 text-sm" : "!px-6 !py-3"
                  }`}
                >
                  {isFullscreen ? "Exit Full Screen" : "Full Screen"}
                </button>
              </div>
  
              <div
  className={`text-center font-semibold text-gray-800 ${
    isLandscape ? "mb-1 text-base" : "mb-4 text-xl md:text-2xl"
  }`}
>
<div className="text-center font-semibold text-gray-800">
  {isMobileView ? (
<div className="text-3xl text-brand-secondary">
  ♥ {remainingLives}
</div>

  ) : (
    <>
      <div
        className={
          isLandscape
            ? "text-base mb-1"
            : isFullscreen
            ? "text-4xl mb-2"
            : "text-2xl mb-2"
        }
      >
        Lives:
      </div>
      <div
        className={`flex justify-center flex-wrap gap-1 ${
          isLandscape
            ? "text-xl"
            : isFullscreen
            ? "text-6xl"
            : "text-4xl"
        }`}
      >
        {Array.from({ length: 10 }).map((_, idx) => (
          <span
            key={idx}
            className={`${
              idx < remainingLives ? "text-brand-secondary" : "text-gray-300"
            } font-[system-ui]`}
          >
            ♥
          </span>
        ))}
      </div>
    </>
  )}
</div>


</div>

  
              <div
                className={`flex items-center justify-center ${
                  isLandscape ? "h-16" : "h-32 md:h-48 mb-6 md:mb-10"
                }`}
              >
                <div className="w-[80%]">
                  <Textfit
                    mode="single"
                    max={isLandscape ? 48 : getDynamicMaxFontSize()}
                    className="text-black font-bold text-center w-full"
                  >
                    {displayWord}
                  </Textfit>
                </div>
              </div>
  
              <div
                className={`flex justify-center ${
                  isLandscape ? "pb-1" : "mt-6 pb-4 md:pb-8"
                }`}
              >
                <div className="w-full max-w-full flex flex-wrap justify-center">
                  {alphabet.map((letter) => (
                    <div
                      key={letter}
                      style={{
                        width: isLandscape
                          ? "calc(100% / 13 - 4px)"
                          : isMobileView
                          ? "calc(100% / 6 - 8px)"
                          : "calc(100% / 13 - 8px)",
                        margin: isLandscape ? "2px" : "4px",
                        aspectRatio: "1/1",
                      }}
                    >
                      <button
                        disabled={
                          guessedLetters.includes(letter) ||
                          gameWon ||
                          remainingLives === 0
                        }
                        onClick={() => handleLetterClick(letter)}
                        style={{
                          width: "100%",
                          height: "100%",
                          padding: "0",
                          margin: "0",
                          boxSizing: "border-box",
                        }}
                        className={`flex items-center justify-center font-semibold border border-gray-400 bg-brand-primary text-white shadow-sm hover:brightness-110 disabled:opacity-40 disabled:bg-gray-300 disabled:text-gray-600 rounded ${
                          isLandscape
                            ? "text-xs md:text-sm"
                            : isFullscreen
                            ? "text-2xl md:text-3xl"
                            : "text-base md:text-lg"
                        }`}
                      >
                        {letter}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
  
          {showModal && (
            <Modal
              onNextWord={handleNextWord}
              onExit={handleExitGame}
              currentWord={currentWord}
              hasMoreWords={words.length > 1}
            />
          )}
          {showGameOverModal && (
            <GameOverModal
              onNextWord={handleNextWord}
              onExit={handleExitGame}
              currentWord={lastWord}
              hasMoreWords={words.length > 1}
            />
          )}
        </div>
      </div>
    </div>
  );
  
  
};

export default App;
